// AbMappingEditor — visueller Feld-Mapping-Editor (Welle 2 "UI-Paritaet", Agent FE-3). Port von
// OC-Pilot MappingView.tsx (NUR gelesen unter scratchpad/OC-Pilot/frontend/src/pages/MappingView.tsx),
// angepasst an die Faehigkeiten von components/pdf/AbPdfViewer.js (WORT-Klick statt freier
// Rechteck-Auswahl auf dem PDF — die wiederverwendbare Viewer-Komponente aus Welle 1 kennt nur
// onWordClick(word), kein Drag-Rechteck; s. dort Kopfkommentar). Orchestriert Formular-State
// (rulesetModel.js), PDF-Fundstellen (wordHighlight.js), Pick-Logik (useMapPick) und KI-Vorschlag
// (useMapAiSuggest) ueber die Tab-Unterkomponenten dieses Ordners. 500-LOC-Regel: State-Hooks
// ausgelagert, Tabs/Actions/Preview in eigene Dateien gesplittet.
//
// Einstiegspunkte (Bauplan Punkt 4):
//  (a) Dokument-Detail-Button "PDF & Feld-Mapping" -> documentId gesetzt, ruleset=null: das aktive
//      Regelset des Dokument-Lieferanten wird automatisch geladen (sonst leeres Formular).
//  (b) Regelset-Verwaltung -> ruleset gesetzt (bereits geladen via getRuleset), documentId=null:
//      ein Testdokument MUSS zuerst gewaehlt werden (Gate), danach identischer Editor.
//
// Admin-Gate: der komplette Editor ist ADMIN-only (Backend erzwingt es bei preview/create/activate
// ohnehin, s. routesAdmin.js) — Nicht-Admins sehen nur einen Hinweis (Bauplan Punkt 4).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, MousePointerClick, X, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Card, Chip, btnGhost, inputCls } from '../components/ui';
import AbPdfViewer from '../components/pdf/AbPdfViewer';
import AbMapSupplierTab from './AbMapSupplierTab';
import AbMapPositionsTab from './AbMapPositionsTab';
import AbMapFieldsTab from './AbMapFieldsTab';
import AbMapJsonTab from './AbMapJsonTab';
import AbMapPreviewTable from './AbMapPreviewTable';
import AbMapActionsBar from './AbMapActionsBar';
import useMapPick from './useMapPick';
import useMapAiSuggest from './useMapAiSuggest';
import { emptyForm, fromRulesetJson, toRulesetObject, toRulesetJson } from './rulesetModel';
import { fieldHighlight, rowAnchorHighlight } from './wordHighlight';
import { getDocument, listDocuments, listRulesets, getRuleset, createRuleset, activateRuleset, previewRuleset, reprocessDocument, getWords } from '../api';

const TABS = ['supplier', 'positions', 'fields', 'json'];

const AbMappingEditor = ({ user, isDarkMode, t, documentId = null, ruleset = null, onBack, onOpenDocument, showSuccess, showError }) => {
  const isAdmin = user?.role === 'admin';

  const [testDocumentId, setTestDocumentId] = useState(documentId || null);
  const [testDoc, setTestDoc] = useState(null);
  const [docList, setDocList] = useState(null); // null = laedt, [] = leer
  const [docPickerValue, setDocPickerValue] = useState('');

  const [form, setForm] = useState(() => (ruleset ? fromRulesetJson(ruleset.rulesJson) : emptyForm()));
  const [loadedMeta, setLoadedMeta] = useState(ruleset ? { id: ruleset.id, version: ruleset.version } : null);
  const [initializing, setInitializing] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [jsonError, setJsonError] = useState(null);

  const [words, setWords] = useState(null);
  const wordsLoadedForRef = useRef(null);

  const [tab, setTab] = useState('fields');
  const [expandedField, setExpandedField] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [savingKind, setSavingKind] = useState(null); // 'activate' | 'draft'

  // Dirty-Check (RULES: kein No-op-Save) — pristineJsonRef haelt den JSON-Stand des zuletzt
  // GELADENEN/GESPEICHERTEN Regelsets; "Speichern"-Buttons bleiben deaktiviert, bis sich am
  // Formular tatsaechlich etwas geaendert hat (auch fuer ein frisch geoeffnetes leeres Formular:
  // vor der ersten Eingabe ist nichts zu speichern).
  const pristineJsonRef = useRef(ruleset ? toRulesetJson(fromRulesetJson(ruleset.rulesJson)) : toRulesetJson(emptyForm()));
  const markPristine = useCallback((f) => { pristineJsonRef.current = toRulesetJson(f); }, []);

  const setField = useCallback((field, patch) => {
    setForm((prev) => ({ ...prev, fields: { ...prev.fields, [field]: { ...prev.fields[field], ...patch } } }));
  }, []);

  // ---- Dokumentliste einmalig laden (Gate + "Testdokument wechseln") ----
  useEffect(() => {
    let alive = true;
    listDocuments(user, { pageSize: 50 }).then((res) => { if (alive) setDocList(Array.isArray(res?.items) ? res.items : []); }).catch(() => { if (alive) setDocList([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Aktives Regelset des Dokument-Lieferanten laden (Einstieg a, ODER Wiederherstellung nach
  // "KI-Vorschlag verwerfen" — s. restoreBaseline unten). Faellt auf ein leeres Formular zurueck,
  // wenn der Lieferant kein aktives Regelset hat bzw. gar kein Lieferant erkannt wurde.
  const loadExistingForDoc = useCallback(async (doc) => {
    if (!doc?.supplierId) {
      const blank = { ...emptyForm(), supplierId: doc?.supplierId || '', supplierName: doc?.supplierName || '' };
      setForm(blank); markPristine(blank); setLoadedMeta(null);
      setInfoMsg(t('ab.map.newRulesetHint'));
      return;
    }
    try {
      const list = await listRulesets(user);
      const active = (Array.isArray(list) ? list : []).find((r) => r.supplierId === doc.supplierId && r.isActive);
      if (active) {
        const full = await getRuleset(user, active.id);
        const loaded = fromRulesetJson(full.rulesJson);
        setForm(loaded); markPristine(loaded);
        setLoadedMeta({ id: full.id, version: full.version });
        setInfoMsg(t('ab.map.existingLoadedMsg', { supplier: full.supplierId, version: full.version }));
        return;
      }
    } catch { /* Lookup best-effort -> unten auf leeres Formular zurueckfallen */ }
    const blank = { ...emptyForm(), supplierId: doc.supplierId || '', supplierName: doc.supplierName || '' };
    setForm(blank); markPristine(blank); setLoadedMeta(null);
    setInfoMsg(t('ab.map.newRulesetHint'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, t]);

  // "KI-Vorschlag verwerfen" stellt den Stand VOR dem KI-Lauf wieder her: bei Einstieg (b) das
  // uebergebene ruleset-Prop, sonst (Einstieg a) das aktive Regelset des Testdokuments erneut laden.
  const restoreBaseline = useCallback(async () => {
    if (ruleset) {
      const loaded = fromRulesetJson(ruleset.rulesJson);
      setForm(loaded); markPristine(loaded);
      setLoadedMeta({ id: ruleset.id, version: ruleset.version });
      return;
    }
    await loadExistingForDoc(testDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleset, testDoc, loadExistingForDoc]);

  const { pick, setPick, startPick, applyPick, pickTargetLabel } = useMapPick({ t, form, setForm, setField });
  const { aiPhase, aiMsg, aiError, aiDraftRulesetId, startAiSuggest, discardAiSuggestion, clearAiDraftRef } =
    useMapAiSuggest({ user, t, testDocumentId, setForm, setLoadedMeta, setPreview, showSuccess, restoreBaseline });

  // ---- Ausgangs-Regelset laden: entweder aus der uebergebenen ruleset-Prop, oder (Einstieg a)
  // das aktive Regelset des Dokument-Lieferanten. Laeuft genau einmal je Mount (Props sind fuer
  // die Lebensdauer der Komponente stabil — der Aufrufer haengt/mounted die Komponente neu aus). ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (documentId) {
          const doc = await getDocument(user, documentId);
          if (!alive) return;
          setTestDoc(doc);
          if (!ruleset) await loadExistingForDoc(doc);
        }
      } catch (e) {
        if (alive) setLoadError(e.message);
      } finally {
        if (alive) setInitializing(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Wortboxen des Testdokuments laden (lazy, je testDocumentId nur einmal). ----
  useEffect(() => {
    if (!testDocumentId || wordsLoadedForRef.current === testDocumentId) return undefined;
    wordsLoadedForRef.current = testDocumentId;
    let alive = true;
    setWords(null);
    getWords(user, testDocumentId).then((w) => { if (alive) setWords(w); }).catch(() => { if (alive) setWords(null); });
    return () => { alive = false; };
  }, [testDocumentId, user]);

  // ---- unsupported-Regelsets werden nur im JSON-Tab bearbeitet -> dorthin wechseln. ----
  useEffect(() => {
    if (form.unsupported && (tab === 'fields' || tab === 'positions')) setTab('json');
  }, [form.unsupported, tab]);

  const changeForm = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);
  const changeRawJson = useCallback((text) => {
    setForm((prev) => ({ ...prev, rawJson: text }));
    try { JSON.parse(text); setJsonError(null); } catch (e) { setJsonError(e.message); }
  }, []);

  const switchTestDocument = (id) => {
    setDocPickerValue(String(id));
    setTestDocumentId(id);
    setWords(null);
    setPreview(null);
    wordsLoadedForRef.current = null;
    getDocument(user, id).then(setTestDoc).catch(() => setTestDoc(null));
  };
  const confirmDocPick = () => { if (docPickerValue) switchTestDocument(Number(docPickerValue)); };

  // ---- Fundstellen im PDF: aktives Feld (Felder-Tab) bzw. Positionsspalten-Anker (Positionen-Tab). ----
  const sourceHighlight = useMemo(() => {
    if (tab === 'fields' && expandedField) return fieldHighlight(words, form, form.fields[expandedField]);
    if (tab === 'positions') return rowAnchorHighlight(words, form);
    return null;
  }, [tab, expandedField, words, form]);
  const highlights = useMemo(() => (sourceHighlight ? [{ id: 'active', page: sourceHighlight.page, boxes: sourceHighlight.boxes, tone: 'active' }] : []), [sourceHighlight]);

  const runPreview = async () => {
    if (!testDocumentId) return;
    setPreviewBusy(true); setPreviewError(null);
    try {
      setPreview(await previewRuleset(user, { rulesJson: toRulesetObject(form), documentId: testDocumentId }));
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  // ---- Speichern (Entwurf oder aktivieren+neu verarbeiten) ----
  const doSave = async (activate) => {
    const supplierId = form.supplierId.trim();
    if (!supplierId) { showError?.(t('ab.map.saveSupplierIdRequired')); return; }
    setSaving(true);
    setSavingKind(activate ? 'activate' : 'draft');
    try {
      const { id } = await createRuleset(user, { supplierId, supplierName: form.supplierName || '', rulesJson: toRulesetObject(form) });
      let version;
      try { const meta = await getRuleset(user, id); version = meta.version; } catch { /* Meta nur fuer die Erfolgsmeldung */ }
      setLoadedMeta({ id, version });
      markPristine(form); // ab jetzt ist der gespeicherte Stand die neue Vergleichsbasis (Dirty-Check)

      if (!activate) {
        showSuccess?.(t('ab.map.saveDraftMsg', { version: version ?? '?' }));
        return;
      }

      await activateRuleset(user, id);
      clearAiDraftRef(id);
      if (testDocumentId) {
        try {
          const r = await reprocessDocument(user, testDocumentId);
          showSuccess?.(t('ab.map.saveActivatedMsg', { version: version ?? '?', status: t(`ab.status.${r.status}`), lineCount: r.lineCount ?? 0 }));
        } catch (e) {
          showSuccess?.(t('ab.map.saveActivatedMsgNoDoc', { version: version ?? '?' }));
          showError?.(t('ab.map.reprocessFailedHint', { error: e.message }));
        }
        if (typeof onOpenDocument === 'function') { onOpenDocument(testDocumentId); return; }
      } else {
        showSuccess?.(t('ab.map.saveActivatedMsgNoDoc', { version: version ?? '?' }));
      }
    } catch (e) {
      showError?.(e.message);
    } finally {
      setSaving(false);
      setSavingKind(null);
    }
  };

  // ---- Rendering ----
  if (!isAdmin) {
    return (
      <div className="space-y-4">
        {typeof onBack === 'function' && (
          <button onClick={onBack} className={btnGhost(isDarkMode)}>
            <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
          </button>
        )}
        <Card isDarkMode={isDarkMode}>
          <p className={`flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />{t('ab.map.adminOnlyTitle')}
          </p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-white/55' : 'text-slate-500'}`}>{t('ab.map.adminOnlyHint')}</p>
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.map.loadError')}: {loadError}</p>
        </Card>
      </div>
    );
  }

  if (initializing) {
    return <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>;
  }

  // ---- Gate: Testdokument muss gewaehlt sein, bevor der Editor selbst erscheint (Einstieg b). ----
  if (!testDocumentId) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <Card isDarkMode={isDarkMode} className="max-w-lg">
          <h2 className={`text-base font-semibold mb-1 ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{t('ab.map.pickDocTitle')}</h2>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-white/55' : 'text-slate-500'}`}>{t('ab.map.pickDocHint')}</p>
          {docList === null ? (
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.map.pickDocLoading')}</p>
          ) : docList.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.map.pickDocEmpty')}</p>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={docPickerValue} onChange={(e) => setDocPickerValue(e.target.value)} className={`${inputCls(isDarkMode)} flex-1 min-w-[220px]`}>
                <option value="">{t('ab.map.pickDocPlaceholder')}</option>
                {docList.map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
              </select>
              <button type="button" disabled={!docPickerValue} onClick={confirmDocPick} className={btnGhost(isDarkMode)}>{t('ab.map.pickDocConfirm')}</button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  const json = toRulesetJson(form);
  const isDirty = json !== pristineJsonRef.current;
  const clickMode = !!pick;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <div>
          <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white/85' : 'text-slate-800'}`}>
            {t('ab.map.title')}
            {loadedMeta?.version != null && <Chip tone="indigo" isDarkMode={isDarkMode}>{t('ab.rulesetVersion', { v: loadedMeta.version })}</Chip>}
          </h2>
          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.map.subtitle')}</p>
        </div>
        <div className="flex-1" />
        {Array.isArray(docList) && docList.length > 0 && (
          <label className="flex items-center gap-2 text-xs">
            <span className={isDarkMode ? 'text-white/50' : 'text-slate-500'}>{t('ab.map.testDocLabel')}</span>
            <select value={testDocumentId} onChange={(e) => switchTestDocument(Number(e.target.value))} className={`${inputCls(isDarkMode)} py-1`}>
              {docList.map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
            </select>
          </label>
        )}
      </div>

      {infoMsg && <p className={`text-xs ${isDarkMode ? 'text-white/45' : 'text-slate-500'}`}>{infoMsg}</p>}

      {form.unsupported && (
        <p className={`text-sm flex items-start gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />{t('ab.map.unsupportedBanner')}
        </p>
      )}

      {pick && (
        <p className={`text-xs flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-amber-500/15 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>
          <MousePointerClick className="w-3.5 h-3.5 flex-shrink-0" />
          {t('ab.map.activePickChip', { target: pickTargetLabel(pick) })}
          <button type="button" onClick={() => setPick(null)} className="ml-1 opacity-70 hover:opacity-100" aria-label={t('ab.map.activePickCancel')}>
            <X className="w-3.5 h-3.5" />
          </button>
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <Card isDarkMode={isDarkMode}>
          <AbPdfViewer
            user={user} isDarkMode={isDarkMode} t={t} documentId={testDocumentId}
            words={words} highlights={highlights} activeHighlightId={sourceHighlight ? 'active' : null}
            clickMode={clickMode} onWordClick={clickMode ? (word) => applyPick(pick, word) : null}
            heightPx={720}
          />
        </Card>

        <div className="space-y-4">
          <Card isDarkMode={isDarkMode}>
            <div className="flex items-center gap-1 border-b pb-2 mb-3 flex-wrap" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }}>
              {TABS.filter((tb) => !form.unsupported || tb === 'supplier' || tb === 'json').map((tb) => (
                <button
                  key={tb} type="button" onClick={() => setTab(tb)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${tab === tb
                    ? (isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700')
                    : (isDarkMode ? 'text-white/55 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')}`}
                >
                  {t(`ab.map.tab.${tb}`)}
                </button>
              ))}
            </div>

            {tab === 'supplier' && <AbMapSupplierTab isDarkMode={isDarkMode} t={t} form={form} onChange={changeForm} unsupported={form.unsupported} />}
            {tab === 'positions' && !form.unsupported && (
              <AbMapPositionsTab isDarkMode={isDarkMode} t={t} form={form} onChange={changeForm} pick={pick} onPick={startPick} />
            )}
            {tab === 'fields' && !form.unsupported && (
              <AbMapFieldsTab
                isDarkMode={isDarkMode} t={t} form={form}
                expandedField={expandedField} onToggleExpand={setExpandedField}
                onFieldChange={setField} pick={pick} onPick={startPick} preview={preview}
              />
            )}
            {tab === 'json' && (
              <AbMapJsonTab isDarkMode={isDarkMode} t={t} json={json} unsupported={form.unsupported} onChangeRawJson={changeRawJson} jsonError={jsonError} />
            )}
          </Card>

          <Card isDarkMode={isDarkMode}>
            <AbMapActionsBar
              isDarkMode={isDarkMode} t={t}
              aiPhase={aiPhase} aiMsg={aiMsg} aiError={aiError} aiDraftActive={!!aiDraftRulesetId}
              onAiSuggest={startAiSuggest} onDiscardAi={discardAiSuggestion}
              previewBusy={previewBusy} previewError={previewError} onPreview={runPreview}
              saving={saving} savingKind={savingKind} onSaveActivate={() => doSave(true)} onSaveDraft={() => doSave(false)}
              testDocumentId={testDocumentId} isDirty={isDirty}
            />
            <div className="mt-4 pt-4 border-t" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }}>
              <AbMapPreviewTable isDarkMode={isDarkMode} t={t} preview={preview} supplierId={form.supplierId} />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AbMappingEditor;
